import { useState } from 'react';
import { ShieldCheck, Ban, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getExperimentVariant } from '../services/analytics';

const KeyValues = () => {
  const { t } = useTranslation();
  const [variant] = useState(() => getExperimentVariant());
  const copyKey = variant === 'privacy_first' ? 'landing.keyValuesPrivacy' : 'landing.keyValues';

  const features = [
    {
      icon: <ShieldCheck className="h-12 w-12 text-blue-600" />,
      title: t(`${copyKey}.hideAddress.title`),
      description: t(`${copyKey}.hideAddress.description`)
    },
    {
      icon: <Ban className="h-12 w-12 text-blue-600" />,
      title: t(`${copyKey}.blockSpam.title`),
      description: t(`${copyKey}.blockSpam.description`)
    },
    {
      icon: <Search className="h-12 w-12 text-blue-600" />,
      title: t(`${copyKey}.trackLeaks.title`),
      description: t(`${copyKey}.trackLeaks.description`)
    }
  ];

  const priorityPages = [
    {
      href: '/best-email-alias-services/',
      label: 'Best email alias services',
      context: 'Category comparison',
    },
    {
      href: '/best-temporary-email-alternatives/',
      label: 'Best temporary email alternatives',
      context: 'Durable alias alternatives',
    },
    {
      href: '/33mail-alternative/',
      label: '33Mail alternative',
      context: 'Legacy alias comparison',
    },
    {
      href: '/adguard-temp-mail-alternative/',
      label: 'AdGuard Temp Mail alternative',
      context: 'Temporary inbox comparison',
    },
    {
      href: '/features/email-forwarding/',
      label: 'Email forwarding feature',
      context: 'One-inbox forwarding',
    },
    {
      href: '/use-cases/saas-signups/',
      label: 'SaaS signup aliases',
      context: 'Use-case workflow',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t(`${copyKey}.title`)}
          </h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            {t(`${copyKey}.subtitle`)}
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="flex flex-col items-center text-center p-6 rounded-xl bg-gray-50 hover:shadow-md transition-shadow"
            >
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-gray-700">{feature.description}</p>
            </div>
          ))}
        </div>

        <div
          aria-label="Priority email alias pages"
          className="mt-12 border-y border-gray-200 py-8"
          role="region"
        >
          <div className="max-w-4xl mx-auto">
            <div className="mb-5 text-center md:text-left">
              <p className="text-sm font-semibold uppercase tracking-wide text-blue-700">
                Compare the pages Google has discovered next
              </p>
              <h3 className="mt-2 text-2xl font-bold text-gray-900">
                Explore private alias guides by decision type
              </h3>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {priorityPages.map((page) => (
                <li key={page.href}>
                  <a
                    aria-label={page.label}
                    className="group flex h-full flex-col justify-between border border-gray-200 bg-white px-4 py-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50"
                    href={page.href}
                  >
                    <span className="font-semibold text-gray-900 group-hover:text-blue-700">
                      {page.label}
                    </span>
                    <span className="mt-1 text-sm text-gray-600">{page.context}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div
          aria-label="Email alias guides"
          className="mt-10 max-w-3xl mx-auto text-center text-gray-700 leading-relaxed"
          role="region"
        >
          <p className="font-semibold text-gray-900 mb-2">Email alias guides</p>
          <p>
            Learn how Uumail works as an{' '}
            <a href="/email-alias-service/" className="text-blue-600 hover:text-blue-700 underline">
              email alias service guide
            </a>
            , when to use a{' '}
            <a href="/private-email-address/" className="text-blue-600 hover:text-blue-700 underline">
              private email address guide
            </a>
            , and how to manage{' '}
            <a href="/multiple-email-addresses/" className="text-blue-600 hover:text-blue-700 underline">
              multiple email addresses guide
            </a>{' '}
            from one inbox, and when to use an{' '}
            <a href="/email-forwarding-alias/" className="text-blue-600 hover:text-blue-700 underline">
              email forwarding alias guide
            </a>
            , and how{' '}
            <a href="/email-forwarding/" className="text-blue-600 hover:text-blue-700 underline">
              email forwarding guide
            </a>{' '}
            keeps alias mail in one inbox, and how a{' '}
            <a href="/catch-all-email/" className="text-blue-600 hover:text-blue-700 underline">
              catch-all email guide
            </a>{' '}
            helps receive aliases created on the fly, and why a{' '}
            <a href="/temporary-email-alternative/" className="text-blue-600 hover:text-blue-700 underline">
              temporary email alternative guide
            </a>{' '}
            fits signups that still need reachable mail, and compare mailbox tags with a{' '}
            <a href="/guides/plus-addressing-vs-email-alias/" className="text-blue-600 hover:text-blue-700 underline">
              plus addressing vs email alias guide
            </a>
            , and use{' '}
            <a href="/custom-domain-email-forwarding/" className="text-blue-600 hover:text-blue-700 underline">
              custom domain email forwarding guide
            </a>{' '}
            helps route private aliases on your own domain, and compare{' '}
            <a href="/uumail-vs-simplelogin/" className="text-blue-600 hover:text-blue-700 underline">
              Uumail vs SimpleLogin comparison
            </a>{' '}
            or{' '}
            <a href="/uumail-vs-addy/" className="text-blue-600 hover:text-blue-700 underline">
              Uumail vs Addy.io comparison
            </a>
            , or{' '}
            <a href="/uumail-vs-firefox-relay/" className="text-blue-600 hover:text-blue-700 underline">
              Uumail vs Firefox Relay comparison
            </a>
            , or{' '}
            <a href="/uumail-vs-duckduckgo/" className="text-blue-600 hover:text-blue-700 underline">
              Uumail vs DuckDuckGo comparison
            </a>
            , or{' '}
            <a href="/uumail-vs-proton-pass/" className="text-blue-600 hover:text-blue-700 underline">
              Uumail vs Proton Pass comparison
            </a>{' '}
            for alias-service choices.
          </p>
          <p className="mt-3">
            For broader buying and replacement research, compare the{' '}
            <a href="/best-email-alias-services/" className="text-blue-600 hover:text-blue-700 underline">
              best email alias services comparison
            </a>
            , review the{' '}
            <a href="/best-temporary-email-alternatives/" className="text-blue-600 hover:text-blue-700 underline">
              best temporary email alternatives comparison
            </a>
            , check the{' '}
            <a href="/33mail-alternative/" className="text-blue-600 hover:text-blue-700 underline">
              33Mail alternative comparison
            </a>
            , and read the{' '}
            <a href="/adguard-temp-mail-alternative/" className="text-blue-600 hover:text-blue-700 underline">
              AdGuard Temp Mail alternative comparison
            </a>
            .
          </p>
          <p className="mt-3">
            Read the{' '}
            <a href="https://uu.me/blog/" className="text-blue-600 hover:text-blue-700 underline">
              Uumail blog privacy guide
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  );
};

export default KeyValues;
